import { zodResolver } from '@hookform/resolvers/zod';
import { atelieSchema, ATELIE_PADRAO, IMAGE_MAX_DIMENSION, type Atelie } from '@plane/shared';
import { useState } from 'react';
import { useFieldArray, useForm } from 'react-hook-form';
import { z } from 'zod';

import { Field } from '@/components/field';
import { CampoDeImagem } from '@/modules/site-content/components/campo-de-imagem';
import { useAtelie, useSaveAtelie } from '@/modules/site-content/hooks/use-conteudo';
import { ApiRequestError } from '@/services/http-client';

/**
 * Formato do formulário.
 *
 * Parágrafos e selos são listas de texto no contrato; aqui viram listas de
 * objetos, que é o que o `useFieldArray` manipula bem.
 */
const formularioSchema = atelieSchema.omit({ paragrafos: true, selos: true }).extend({
  paragrafos: z
    .array(z.object({ texto: z.string().trim().min(1).max(800) }))
    .min(1)
    .max(4),
  selos: z.array(z.object({ texto: z.string().trim().min(1).max(40) })).max(8),
});

type Formulario = z.infer<typeof formularioSchema>;

const paraFormulario = (atelie: Atelie): Formulario => ({
  ...atelie,
  paragrafos: atelie.paragrafos.map((texto) => ({ texto })),
  selos: atelie.selos.map((texto) => ({ texto })),
});

const paraAtelie = (valores: Formulario): Atelie => ({
  ...valores,
  paragrafos: valores.paragrafos.map((p) => p.texto),
  selos: valores.selos.map((s) => s.texto),
});

function mensagemDeErro(erro: unknown): string {
  if (erro instanceof ApiRequestError) return erro.message;
  if (erro instanceof Error) return erro.message;

  return 'Não foi possível falar com o servidor. Verifique a conexão e tente de novo.';
}

export function AteliePage() {
  const consulta = useAtelie();
  const salvar = useSaveAtelie();
  const [erro, setErro] = useState<string | null>(null);
  const [salvo, setSalvo] = useState(false);

  if (consulta.isPending) return <p className="text-sm text-ink-soft">Carregando…</p>;

  if (consulta.isError) {
    return (
      <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
        {mensagemDeErro(consulta.error)}
      </p>
    );
  }

  return (
    <Formulario
      key={consulta.data ? 'salvo' : 'padrao'}
      inicial={consulta.data ?? ATELIE_PADRAO}
      inedito={consulta.data === null}
      erro={erro}
      salvo={salvo}
      salvando={salvar.isPending}
      aoEnviar={(atelie) => {
        setErro(null);
        setSalvo(false);

        salvar
          .mutateAsync(atelie)
          .then(() => setSalvo(true))
          .catch((falha: unknown) => setErro(mensagemDeErro(falha)));
      }}
    />
  );
}

function Formulario({
  inicial,
  inedito,
  erro,
  salvo,
  salvando,
  aoEnviar,
}: {
  inicial: Atelie;
  inedito: boolean;
  erro: string | null;
  salvo: boolean;
  salvando: boolean;
  aoEnviar: (atelie: Atelie) => void;
}) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<Formulario>({
    resolver: zodResolver(formularioSchema),
    defaultValues: paraFormulario(inicial),
  });

  const paragrafos = useFieldArray({ control, name: 'paragrafos' });
  const selos = useFieldArray({ control, name: 'selos' });
  const numeros = useFieldArray({ control, name: 'numeros' });

  const fotoPrincipal = watch('fotoPrincipal');
  const fotoSecundaria = watch('fotoSecundaria');

  return (
    <form
      onSubmit={(evento) => {
        void handleSubmit((valores) => aoEnviar(paraAtelie(valores)))(evento);
      }}
      className="flex max-w-2xl flex-col gap-6"
    >
      <div>
        <p className="text-xs tracking-[0.18em] text-brass uppercase">Conteúdo do site</p>
        <h1 className="mt-2 font-display text-2xl">Ateliê</h1>
        <p className="mt-3 text-sm text-ink-soft">
          A história da marcenaria, as duas fotos da fábrica e os números.
        </p>
      </div>

      {inedito && (
        <p className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          Esta seção ainda não foi salva. Os campos estão com o conteúdo do protótipo —{' '}
          <strong>
            os quatro números, a história do Seu Antônio e a metragem da fábrica são dado de
            demonstração
          </strong>{' '}
          e precisam da sua conferência.
        </p>
      )}

      <Field
        id="kicker"
        label="Etiqueta acima do título"
        placeholder="01 — O ateliê"
        error={errors.kicker?.message}
        {...register('kicker')}
      />

      <AreaDeTexto
        id="titulo"
        rotulo="Título"
        linhas={2}
        erro={errors.titulo?.message}
        ajuda={
          <>
            Quebre a linha onde o título deve quebrar. <code>*assim*</code> destaca a palavra em
            verde itálico.
          </>
        }
        {...register('titulo')}
      />

      <Lista
        titulo="Parágrafos"
        nota="O primeiro aparece maior, como abertura da seção."
        campos={paragrafos.fields}
        aoRemover={paragrafos.fields.length > 1 ? (i) => paragrafos.remove(i) : undefined}
        aoAdicionar={
          paragrafos.fields.length < 4 ? () => paragrafos.append({ texto: '' }) : undefined
        }
        rotuloAdicionar="Adicionar parágrafo"
        renderizar={(indice) => (
          <AreaDeTexto
            id={`paragrafo-${String(indice)}`}
            rotulo={`Parágrafo ${String(indice + 1)}`}
            linhas={4}
            erro={errors.paragrafos?.[indice]?.texto?.message}
            {...register(`paragrafos.${indice}.texto`)}
          />
        )}
      />

      <Lista
        titulo="Selos"
        nota="As etiquetas arredondadas ao lado do texto."
        campos={selos.fields}
        aoRemover={(i) => selos.remove(i)}
        aoAdicionar={selos.fields.length < 8 ? () => selos.append({ texto: '' }) : undefined}
        rotuloAdicionar="Adicionar selo"
        renderizar={(indice) => (
          <Field
            id={`selo-${String(indice)}`}
            label={`Selo ${String(indice + 1)}`}
            placeholder="Usinagem CNC própria"
            error={errors.selos?.[indice]?.texto?.message}
            {...register(`selos.${indice}.texto`)}
          />
        )}
      />

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Foto principal
        </legend>
        <p className="mt-1.5 text-xs text-ink-soft">A larga, no alto da seção. Fica bem deitada.</p>

        <div className="mt-3">
          <CampoDeImagem
            mediaId={fotoPrincipal}
            maiorLado={IMAGE_MAX_DIMENSION}
            aoTrocar={(id) => setValue('fotoPrincipal', id, { shouldValidate: true })}
          />
        </div>

        <div className="mt-4">
          <Field
            id="cota"
            label="Cota escrita sobre a foto"
            placeholder="1.400 m² de fábrica"
            error={errors.cota?.message}
            {...register('cota')}
          />
          <p className="mt-1.5 text-xs text-ink-soft">Deixe vazio para não escrever nada.</p>
        </div>
      </fieldset>

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">
          Foto secundária
        </legend>
        <p className="mt-1.5 text-xs text-ink-soft">A menor, embaixo. Fica em pé.</p>

        <div className="mt-3">
          <CampoDeImagem
            mediaId={fotoSecundaria}
            maiorLado={IMAGE_MAX_DIMENSION}
            aoTrocar={(id) => setValue('fotoSecundaria', id, { shouldValidate: true })}
          />
        </div>
      </fieldset>

      <fieldset className="m-0 border-0 p-0">
        <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">Números</legend>
        <p className="mt-1.5 text-xs text-ink-soft">
          Contam de zero até o valor quando aparecem na tela.
        </p>

        <ul className="mt-3 flex list-none flex-col gap-4 p-0">
          {numeros.fields.map((campo, indice) => (
            <li
              key={campo.id}
              className="grid gap-3 sm:grid-cols-[6rem_4rem_1fr_auto] sm:items-end"
            >
              <Field
                id={`numero-valor-${String(indice)}`}
                label="Valor"
                inputMode="numeric"
                error={errors.numeros?.[indice]?.valor?.message}
                {...register(`numeros.${indice}.valor`, { valueAsNumber: true })}
              />
              <Field
                id={`numero-sufixo-${String(indice)}`}
                label="Sinal"
                placeholder="+"
                error={errors.numeros?.[indice]?.sufixo?.message}
                {...register(`numeros.${indice}.sufixo`)}
              />
              <Field
                id={`numero-rotulo-${String(indice)}`}
                label="Descrição"
                placeholder="ambientes entregues"
                error={errors.numeros?.[indice]?.rotulo?.message}
                {...register(`numeros.${indice}.rotulo`)}
              />
              <button
                type="button"
                onClick={() => numeros.remove(indice)}
                className="self-start text-xs text-brass underline sm:self-auto sm:pb-3"
              >
                Remover
              </button>
            </li>
          ))}
        </ul>

        {numeros.fields.length < 6 && (
          <button
            type="button"
            onClick={() => numeros.append({ valor: 0, sufixo: '', rotulo: '' })}
            className="mt-3 text-sm text-brand-700 underline"
          >
            Adicionar número
          </button>
        )}
      </fieldset>

      {erro && (
        <p role="alert" className="border border-brass bg-bone-soft px-3 py-2.5 text-sm">
          {erro}
        </p>
      )}

      {salvo && (
        <p role="status" className="border-l-2 border-brand-600 pl-3 text-sm text-brand-700">
          Salvo. O site já mostra o conteúdo novo.
        </p>
      )}

      <button
        type="submit"
        disabled={salvando}
        className="self-start bg-brand-600 px-5 py-2.5 text-sm text-white transition-opacity disabled:opacity-50"
      >
        {salvando ? 'Salvando…' : 'Salvar'}
      </button>
    </form>
  );
}

function Lista({
  titulo,
  nota,
  campos,
  renderizar,
  aoRemover,
  aoAdicionar,
  rotuloAdicionar,
}: {
  titulo: string;
  nota?: string;
  campos: readonly { id: string }[];
  renderizar: (indice: number) => React.ReactNode;
  aoRemover?: ((indice: number) => void) | undefined;
  aoAdicionar?: (() => void) | undefined;
  rotuloAdicionar: string;
}) {
  return (
    <fieldset className="m-0 border-0 p-0">
      <legend className="text-xs tracking-[0.12em] text-ink-soft uppercase">{titulo}</legend>
      {nota && <p className="mt-1.5 text-xs text-ink-soft">{nota}</p>}

      <ul className="mt-3 flex list-none flex-col gap-4 p-0">
        {campos.map((campo, indice) => (
          <li key={campo.id} className="flex items-end gap-3">
            <div className="flex-1">{renderizar(indice)}</div>
            {aoRemover && (
              <button
                type="button"
                onClick={() => aoRemover(indice)}
                className="pb-3 text-xs text-brass underline"
              >
                Remover
              </button>
            )}
          </li>
        ))}
      </ul>

      {aoAdicionar && (
        <button
          type="button"
          onClick={aoAdicionar}
          className="mt-3 text-sm text-brand-700 underline"
        >
          {rotuloAdicionar}
        </button>
      )}
    </fieldset>
  );
}

function AreaDeTexto({
  id,
  rotulo,
  linhas,
  erro,
  ajuda,
  ref,
  ...resto
}: {
  id: string;
  rotulo: string;
  linhas: number;
  erro?: string | undefined;
  ajuda?: React.ReactNode;
} & React.ComponentPropsWithRef<'textarea'>) {
  const idAjuda = ajuda ? `${id}-ajuda` : undefined;

  return (
    <div className="flex flex-col gap-1.5">
      <label htmlFor={id} className="text-xs tracking-[0.12em] text-ink-soft uppercase">
        {rotulo}
      </label>

      <textarea
        {...resto}
        ref={ref}
        id={id}
        rows={linhas}
        aria-invalid={erro ? true : undefined}
        aria-describedby={idAjuda}
        className="border border-clay bg-white px-3 py-2.5 text-sm transition-colors focus:border-brand-600 focus:outline-none aria-invalid:border-brass"
      />

      {ajuda && (
        <p id={idAjuda} className="text-xs text-ink-soft">
          {ajuda}
        </p>
      )}

      {erro && (
        <p role="alert" className="text-xs text-brass">
          {erro}
        </p>
      )}
    </div>
  );
}
